// modules/website/hooks/useTenant.ts - Hook para acessar dados do tenant atual

import { useState, useEffect } from 'react';
import { Tenant } from '../../../core/types';
import { getCurrentTenantId, tenantService } from '../../../core/services/firebase/firestore';

// Dados mock de tenant para desenvolvimento local
const DEMO_TENANT = {
  id: 'demo',
  businessName: 'Pousada Demo',
  domain: 'localhost:5173',
  subdomainStaging: 'demo.nossapesca.com',
  plan: 'premium',
  active: true,
  description: 'Bem-vindo à Nossa Pesca! Esta é uma demonstração da plataforma. Personalize este site com sua marca.',
  branding: {
    primaryColor: '#0ea5e9',
    secondaryColor: '#0284c7',
    accentColor: '#f59e0b',
    logoUrl: '',
    faviconUrl: '',
    fontFamily: 'Inter',
    headerStyle: 'transparent',
    footerStyle: 'dark'
  },
  settings: {
    language: 'pt-BR',
    currency: 'BRL',
    timezone: 'America/Sao_Paulo',
    enableChat: true,
    enableReservations: true,
    enablePayments: false
  },
  seoTitle: 'Nossa Pesca | Demonstração',
  seoDescription: 'Plataforma completa para pousadas e operadores de pesca esportiva. Crie seu site profissional em minutos.',
  pages: [
    {
      id: 'home',
      slug: '',
      title: 'Início',
      isHomepage: true,
      publishedAt: new Date().toISOString(),
      components: [
        {
          id: 'hero-1',
          type: 'hero',
          order: 0,
          settings: {
             backgroundColor: '#1e40af'
          },
          data: {
            title: 'Sua Pousada Aqui',
            subtitle: 'A melhor experiência de pesca esportiva. Personalize este site com sua marca.',
            backgroundImage: 'https://images.unsplash.com/photo-1545816250-e12bedba42ba?w=1920&q=80',
            buttons: [
              {
                text: 'Reserve Agora',
                url: '/reservas',
                variant: 'primary'
              }
            ]
          }
        },
        {
          id: 'about-1',
          type: 'rich_text',
          order: 1,
          settings: {
            backgroundColor: '#f3f4f6',
            padding: '4rem 0'
          },
          data: {
            content: `
              <div style="text-align: center;">
                <h2 style="font-size: 2.25rem; font-weight: bold; margin-bottom: 1.5rem;">Sobre a Pousada</h2>
                <p style="font-size: 1.125rem; color: #4b5563; max-width: 48rem; margin: 0 auto; line-height: 1.75;">
                  Este é um texto de demonstração. Aqui você pode descrever sua pousada, sua história, sua estrutura e diferenciais.
                  Edite este conteúdo no painel administrativo para contar o que torna seu destino único.
                </p>
                <div style="margin-top: 2rem; display: flex; justify-content: center; gap: 2rem;">
                  <span style="background: white; padding: 0.5rem 1rem; border-radius: 0.5rem; shadow: 0 1px 2px rgba(0,0,0,0.1);">✓ Acomodações confortáveis</span>
                  <span style="background: white; padding: 0.5rem 1rem; border-radius: 0.5rem; shadow: 0 1px 2px rgba(0,0,0,0.1);">✓ Guias experientes</span>
                  <span style="background: white; padding: 0.5rem 1rem; border-radius: 0.5rem; shadow: 0 1px 2px rgba(0,0,0,0.1);">✓ Barcos equipados</span>
                </div>
              </div>
            `,
            alignment: 'center'
          }
        },
        {
          id: 'services-1',
          type: 'services',
          order: 2,
          settings: {
             backgroundColor: '#ffffff'
          },
          data: {
            title: 'Nossos Serviços',
            subtitle: 'O que oferecemos para sua pescaria',
            columns: 4,
            features: [
              { icon: '🎣', title: 'Pesca Esportiva', description: 'Pescarias guiadas com profissionais experientes.' },
              { icon: '🏨', title: 'Hospedagem', description: 'Quartos confortáveis para seu descanso.' },
              { icon: '🍽️', title: 'Gastronomia', description: 'Culinária regional de primeira qualidade.' },
              { icon: '🚤', title: 'Equipamentos', description: 'Barcos e equipamentos de pesca modernos.' }
            ]
          }
        },
        {
          id: 'gallery-1',
          type: 'gallery',
          order: 3,
          settings: {
            backgroundColor: '#f9fafb'
          },
          data: {
            title: 'Galeria de Troféus',
            columns: 4,
            gap: 4,
            images: [
              { src: 'https://images.unsplash.com/photo-1544551763-46a013bb70d5?w=600&q=80', alt: 'Pesca esportiva' },
              { src: 'https://images.unsplash.com/photo-1559583985-c80d8ad9b29f?w=600&q=80', alt: 'Troféu capturado' },
              { src: 'https://images.unsplash.com/photo-1534575680889-a839fc8c8421?w=600&q=80', alt: 'Paisagem do rio' },
              { src: 'https://images.unsplash.com/photo-1540979388789-6cee28a1cdc9?w=600&q=80', alt: 'Pôr do sol' }
            ]
          }
        },
        {
          id: 'contact-1',
          type: 'contact_form',
          order: 4,
          settings: {
            backgroundColor: '#ffffff'
          },
          data: {
            title: 'Entre em Contato',
            fields: ['name', 'email', 'phone', 'message'],
            submitText: 'Enviar Mensagem',
            successMessage: 'Mensagem enviada com sucesso! Entraremos em contato em breve.'
          }
        }
      ]
    }
  ],
  navigation: [
    { label: 'Início', href: '/', order: 0 },
    { label: 'Sobre', href: '/sobre', order: 1 },
    { label: 'Pacotes', href: '/pacotes', order: 2 },
    { label: 'Galeria', href: '/galeria', order: 3 },
    { label: 'Contato', href: '/contato', order: 4 }
  ],
  contactInfo: {
    phone: '(00) 00000-0000',
    email: 'contato@suapousada.com.br',
    whatsapp: '5500000000000',
    address: 'Sua Cidade - Estado, Brasil'
  },
  socialLinks: {
    instagram: 'https://instagram.com/nossapesca_com',
    facebook: 'https://facebook.com/nossapesca',
    youtube: ''
  },
  createdAt: new Date().toISOString(),
  updatedAt: new Date().toISOString()
};

const isDevelopment = () => {
  const hostname = window.location.hostname;
  return hostname === 'localhost' || hostname === '127.0.0.1';
};

export const useTenant = () => {
  const [tenant, setTenant] = useState<Tenant | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadTenant();
  }, []);

  const loadTenant = async () => {
    try {
      setLoading(true);
      const tenantId = getCurrentTenantId();
      
      if (!tenantId || tenantId === 'platform' || tenantId === 'builder') {
        setTenant(null);
        setLoading(false);
        return;
      }

      // Em desenvolvimento, usar dados mock
      if (isDevelopment() && (tenantId === 'demo' || tenantId === 'default')) {
        console.log('🔧 Usando tenant de demonstração (desenvolvimento)');
        setTenant(DEMO_TENANT as unknown as Tenant);
        setLoading(false);
        return;
      }

      // Buscar dados do tenant do Firestore
      const tenantData = await tenantService.getTenant(tenantId);
      
      if (tenantData) {
        setTenant(tenantData as Tenant);
      } else {
        // Fallback para demo em desenvolvimento
        if (isDevelopment()) {
          console.log('🔧 Tenant não encontrado, usando demo');
          setTenant(DEMO_TENANT as unknown as Tenant);
        } else {
          setError('Tenant não encontrado');
        }
      }
    } catch (err) {
      console.error('Erro ao carregar tenant:', err);
      // Fallback para demo em desenvolvimento
      if (isDevelopment()) {
        console.log('🔧 Erro no Firestore, usando demo');
        setTenant(DEMO_TENANT as unknown as Tenant);
      } else {
        setError('Erro ao carregar site');
      }
    } finally {
      setLoading(false);
    }
  };

  const refreshTenant = () => {
    loadTenant();
  };

  return {
    tenant,
    loading,
    error,
    refreshTenant
  };
};
